export default {
  common: {
    change: 'Change',
    or: 'or',
    back: 'Back',
    continue: 'Continue',
    backToLogin: 'Back to sign in',
  },
  fields: {
    name: 'Full name',
    email: 'Email address',
    password: 'Password',
    newPassword: 'Password (8 characters minimum)',
    confirmPassword: 'Confirm password',
  },
  remembered: {
    forget: 'Forget this account',
    continueAs: 'Continue as {name}',
    useDifferent: 'Use a different account',
  },
  login: {
    welcomeBack: 'Welcome back',
    continueWhereYouLeftOff: 'Continue where you left off',
    rememberAddress: 'Remember my address on this browser',
    title: 'Sign in',
    subtitle: 'Welcome to {platform}.',
    submit: 'Sign in',
    forgotPassword: 'Forgot password?',
    noAccount: 'No account yet?',
    signUp: 'Create one',
    noAccess: 'Not registered yet?',
    requestAccess: 'Request registration',
    continueWith: 'Continue with {provider}',
    ssoFailed: 'Signing in with that provider did not work. Try again, or use your password.',
    enrolled: 'Two-step verification is on. Sign in again: you will be asked for a code from your app.',
  },
  mfa: {
    title: 'Two-step verification',
    subtitle: 'Enter the six-digit code from your authenticator app, or one of your recovery codes.',
    code: 'Code',
    submit: 'Verify',
  },
  enrol: {
    title: 'Set up two-step verification',
    required:
      'This platform requires two-step verification, and this account has not set it up yet. Set it up to finish signing in.',
    scan: 'Scan this code with your authenticator app, or type the key into it.',
    secret: 'Key',
    code: 'Six-digit code from the app',
    submit: 'Turn on',
    restart: 'Show a new code',
    invalidCode: 'That code did not work. Check that the time on your phone is right, and try again.',
    expired: 'This set-up has expired. Sign in again to start over.',
    alreadyEnrolled: 'Two-step verification is already on for this account.',
    unavailable: 'Two-step verification is not available on this platform.',
  },
  recoveryCodes: {
    title: 'Your recovery codes',
    explain:
      'Each code opens your account once if you lose your phone. Keep them somewhere safe: they will not be shown again.',
    copy: 'Copy',
    copied: 'Copied',
    saved: 'I have saved them',
  },
  reauth: {
    password: 'Current password',
    code: 'Code from your authenticator app, or a recovery code',
    wrongPassword: 'Incorrect password.',
    wrongCode: 'That code did not work. Try again.',
  },
  signup: {
    title: 'Create your account',
    subtitle: 'Join {platform}.',
    submit: 'Create account',
    sentTitle: 'Check your inbox',
    sent: 'If {email} can be registered, a message with a code and a link is on its way. Open the link, or sign in to enter the code.',
    closed: 'Registration is not open on this platform. Ask an administrator for an invitation.',
    haveAccount: 'Already have an account?',
    signIn: 'Sign in',
  },
  verify: {
    title: 'Confirm your email address',
    verifying: 'Confirming…',
    verified: 'Your address is confirmed.',
    pending: 'We sent a code to {email}. Enter it below, or open the link in the same message.',
    code: 'Confirmation code',
    submit: 'Confirm',
    resend: 'Send a new code',
    resent: 'A new message is on its way.',
    invalid: 'This code or link is not valid any more. Ask for a new one.',
    openTheLink: 'Open the link from the confirmation message to finish.',
  },
  emailChange: {
    confirmTitle: 'Confirm your new address',
    confirming: 'Confirming…',
    confirmed: 'Your address has changed. Your previous address was told, with a way to undo it.',
    invalid: 'This link is not valid any more. Ask for the change again from your profile.',
    openTheLink: 'Open the link from the confirmation message sent to your new address.',
    revertTitle: 'Undo the address change',
    revertExplain:
      'If you did not ask for this change, restore your previous address. Every session will be signed out.',
    revertSubmit: 'Restore my previous address',
    reverted: '{email} is your address again. Choose a new password now: whoever made the change knew the old one.',
    revertedSignIn: '{email} is your address again. Sign in with it.',
    revertInvalid: 'This link is not valid any more.',
    choosePassword: 'Set the new password',
    passwordSet: 'Your password has been changed. Sign in with {email}.',
  },
  invitation: {
    title: 'Join {org}',
    opening: 'Opening your invitation…',
    invitedBy: '{inviter} invited {email} to join {org} as {role}.',
    invited: '{email} is invited to join {org} as {role}.',
    expires: 'This invitation expires on {date}.',
    accept: 'Accept the invitation',
    wrongAccount:
      'You are signed in as {current}, but this invitation is for {email}. Sign out, then open the link again.',
    signOut: 'Sign out',
    signIn: 'Sign in to accept',
    createTitle: 'Create your account to join {org}.',
    create: 'Create my account and join',
    accountExists: 'You already have an account with {email}. Sign in to join {org}.',
    invalid:
      'This invitation is not valid any more: it expired, was withdrawn or was already accepted. Ask for a new one.',
    missingToken: 'Open the link from the invitation message.',
    joined: 'You have joined {org}.',
    alreadyMember: 'You already belong to {org}.',
    open: 'Open {org}',
  },
  join: {
    opening: 'Opening the form…',
    subtitle:
      'Ask to register with {org}. An administrator will review your request; if it is accepted, you will receive an invitation by email.',
    requiredLegend: 'Fields marked with * are required.',
    linkPlaceholder: 'www.example.com',
    terms: 'I accept the {link}',
    termsLink: 'terms of use',
    honeypot: 'Leave this field empty',
    submit: 'Send request',
    sentTitle: 'Request sent',
    sent: 'Your request has been sent. If it is accepted, you will receive an invitation by email.',
    unavailableTitle: 'Form unavailable',
    unavailable: 'This form is not available.',
    loadFailedTitle: 'The form could not be loaded',
    retry: 'Try again',
    haveAccount: 'Already a member?',
    refused: 'Your request could not be recorded. Correct the form, then try again.',
    tooLarge: 'Your answers are too long. Shorten them, then try again.',
    rateLimited: 'Too many requests were sent from your connection. Try again in an hour.',
    errors: {
      required: 'This field is required.',
      choose: 'Choose an option.',
      email: "Enter a valid email address, for example name{'@'}example.com.",
      tooLong: '{max} characters at most.',
      number: 'Enter a number.',
      atLeast: 'At least {min}.',
      atMost: 'At most {max}.',
      date: 'Enter a date.',
      link: 'Enter the address of a website, for example www.example.com.',
      terms: 'Accept the terms to send your request.',
    },
  },
  forgot: {
    title: 'Forgot your password?',
    subtitle: 'Enter your email address and we will send you a link to choose a new one.',
    submit: 'Send the link',
    sent: 'If an account exists for {email}, a reset link is on its way.',
    closed: 'Password reset is not available on this platform. Contact your administrator.',
  },
  reset: {
    title: 'Choose a new password',
    subtitle: 'You will be signed out everywhere, then you can sign in with the new password.',
    submit: 'Change password',
    done: 'Your password has been changed.',
    invalid: 'This reset link is not valid any more. Ask for a new one.',
  },
  organizations: {
    title: 'Your organizations',
    signedInAs: 'Signed in as {email}',
    none: 'You do not belong to any organization yet. Ask an administrator to invite you.',
    signOut: 'Sign out',
  },
  founding: {
    title: 'Create your organization',
    intro: 'An organization brings together your videos and the people you work with. Give it a name to get started.',
    name: 'Organization name',
    defaultName: "{name}'s organization",
    defaultWorkspace: 'My space',
    submit: 'Create the organization',
    another: 'Create an organization',
    limitReached: 'You already own the most organizations this platform allows ({limit}).',
    unverified: 'Confirm your email address to create an organization.',
    nameRequired: 'Give the organization a name.',
    nameTaken: 'Too many organizations on this platform already have this name. Choose another one.',
  },
  // An address the platform will not take (`addressRefusal.ts`): it never says which rule refused
  // it. A misspelt webmail is the exception, since the correction is the whole message.
  addressRefusal: {
    signupLasting:
      'This email address cannot be used here. Sign up with a lasting address: your work address, or one from a mailbox you keep.',
    signupMisspelt: 'Did you mean {address}? Correct the address: this one would not receive our confirmation email.',
    // Inside an organization the address is changed in the profile's settings; without one, the
    // person can only open an account at another address.
    foundingLasting:
      'Your account’s email address cannot be used to create an organization here. Replace it with a lasting address, your work address or one from a mailbox you keep, in your profile settings.',
    foundingLastingNoOrganization:
      'Your account’s email address cannot be used to create an organization here. Create an account with a lasting address, your work address or one from a mailbox you keep, then the organization from that account.',
    foundingMisspelt:
      'Your account’s email address looks misspelt: did you mean {address}? Correct it in your profile settings, then create the organization.',
    foundingMisspeltNoOrganization:
      'Your account’s email address looks misspelt: did you mean {address}? Create an account at that address, then the organization from that account.',
  },
  errors: {
    credentials: 'Incorrect email address or password.',
    rateLimited: 'Too many attempts. Wait a moment and try again.',
    passwordLoginClosed: 'Password sign-in is not available on this platform.',
    mailUnavailable: 'Email cannot be sent from this platform right now. Try again later.',
    passwordTooShort: 'At least {min} characters.',
    passwordMismatch: 'The two passwords differ.',
    network: 'The server could not be reached. Check your connection and try again.',
    generic: 'Something went wrong. Try again.',
  },
}
